import type { Web3TxMultiCalcSWR } from "@/hooks/tx-flow/useDepositCalculate";
import type { SupTransactionType, SupWeb3TxSingleVault } from "@/types";
import React, { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";

import prepareProviderRoutes, { type SupSinglePreparedRoute } from "@/components/Web3/Common/Route/utils/prepareProviderRoutes";
import { getWeb3TxRoutes } from "@/components/Web3/Common/Route/utils/getWeb3TxRoutes";
import { processApiError, txVaultBridgeError } from "@/utils";

function setVaultDefaultSelectedRoute(singleVault: SupWeb3TxSingleVault, txCalculate: Web3TxMultiCalcSWR | undefined, type: SupTransactionType): SupWeb3SelectedRoute {
  if (!txCalculate) {
    return {
      vaultId: singleVault.id,
      selectedRoute: undefined,
      error: undefined,
    };
  }

  if (txCalculate.isError) {
    return {
      vaultId: singleVault.id,
      selectedRoute: undefined,
      error: new Error(
        txCalculate.error
          ? processApiError(txCalculate.error, "Please select a DIFFERENT token, re-calculate the route or remove this vault.")
          : "Unknown error during the route calculation"
      ),
    };
  }

  if (txCalculate.isLoading) {
    return {
      vaultId: singleVault.id,
      selectedRoute: undefined,
      error: undefined,
    };
  }

  const { calculateError, isError, calculatedRoute } = getWeb3TxRoutes(singleVault.id, txCalculate);

  if (isError || calculatedRoute?.routes.length === 0) {
    if (!calculatedRoute?.in.is_same_route) {
      return {
        vaultId: singleVault.id,
        selectedRoute: undefined,
        error: calculatedRoute ? txVaultBridgeError(calculatedRoute, type) : calculateError ? calculateError : new Error("Unknown error during the route calculation"),
      };
    } else {
      return {
        vaultId: singleVault.id,
        selectedRoute: "direct",
        error: undefined,
      };
    }
  }
  const processedRoutes = prepareProviderRoutes(calculatedRoute, singleVault, type);
  return {
    vaultId: singleVault.id,
    selectedRoute: processedRoutes[0],
    error: undefined,
  };
}

export interface SupWeb3SelectedRoute {
  selectedRoute: SupSinglePreparedRoute | "direct" | undefined;
  error: Error | undefined;
  vaultId: string;
}

export interface Web3CommonRouteState {
  txCalculate: Web3TxMultiCalcSWR | undefined;
  selectedRoutes: SupWeb3SelectedRoute[];
  putVaultSelectedRoute?: (vaultId: string, route: SupSinglePreparedRoute) => void;
  getVaultSelectedRoute?: (vaultId: string) => SupWeb3SelectedRoute | undefined;
}

const RouteState = createContext<Web3CommonRouteState>({
  txCalculate: undefined,
  selectedRoutes: [],
});

interface Web3CommonRouteStateProviderProps extends PropsWithChildren {
  type: SupTransactionType;
  txCalculate: Web3TxMultiCalcSWR | undefined;
  vaultList: SupWeb3TxSingleVault[];
}

export default function RouteStateProvider({ children, type, txCalculate, vaultList }: Web3CommonRouteStateProviderProps) {
  const [selectedRoutes, setSelectedRoute] = useState<SupWeb3SelectedRoute[]>(vaultList.map(({ id }) => ({ selectedRoute: undefined, error: undefined, vaultId: id })));

  useEffect(() => {
    setSelectedRoute(vaultList.map(singleVault => setVaultDefaultSelectedRoute(singleVault, txCalculate, type)));
  }, [txCalculate, type, vaultList]);

  const putVaultSelectedRoute = useCallback((vaultId: string, route: SupSinglePreparedRoute) => {
    setSelectedRoute(currentSelectedRoutes => [
      ...currentSelectedRoutes.filter(single => single.vaultId !== vaultId),
      { vaultId: vaultId, selectedRoute: route, error: undefined },
    ]);
  }, []);

  const getVaultSelectedRoute = useCallback((vaultId: string) => selectedRoutes.find(item => item.vaultId === vaultId), [selectedRoutes]);

  const contextValues = useMemo(
    () => ({
      selectedRoutes,
      txCalculate,
      getVaultSelectedRoute,
      putVaultSelectedRoute,
    }),
    [getVaultSelectedRoute, putVaultSelectedRoute, selectedRoutes, txCalculate]
  );

  return <RouteState.Provider value={contextValues}>{children}</RouteState.Provider>;
}

export function useRouteState() {
  return useContext(RouteState);
}
